import { Box, Group, Stack, Text, ActionIcon, rem } from '@mantine/core'
import { motion } from 'framer-motion'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import {
  peopleRowActionsTransition,
  peopleRowActionsVariants,
  peopleRowAvatarTransition,
  peopleRowAvatarVariants,
  peopleRowBackgroundTransition,
  peopleRowBackgroundVariants
} from '../animations'

export type PeopleRowAction = {
  icon: ReactNode
  label: string
  color?: string
  onClick?: () => void
}

export type PeopleRowProps = {
  name: string
  subtitle: string
  // The avatar to render (e.g. an authed UserAvatar).
  avatar: ReactNode
  avatarBadge?: ReactNode
  actions?: PeopleRowAction[]
  // When provided, the whole row becomes a link to this path.
  to?: string
}

export const PeopleRow = ({
  name,
  subtitle,
  avatar,
  avatarBadge,
  actions,
  to
}: PeopleRowProps) => {
  const content = (
    <Box
      component={motion.div}
      initial="initial"
      p="xs"
      w="100%"
      whileHover="hover"
      variants={peopleRowBackgroundVariants}
      transition={peopleRowBackgroundTransition}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group
          gap="sm"
          align="flex-start"
          wrap="nowrap"
          flex={1}
          style={{ inWidth: 0 }}
        >
          <Box pos="relative" className="flex-shrink-0">
            <motion.div
              variants={peopleRowAvatarVariants}
              transition={peopleRowAvatarTransition}
            >
              {avatar}
            </motion.div>

            {avatarBadge && (
              <Box pos="absolute" bottom={-2} right={-2}>
                {avatarBadge}
              </Box>
            )}
          </Box>

          <Stack gap={0} flex="1" style={{ minWidth: 0 }}>
            <Text
              fw={700}
              size="sm"
              c="gray.9"
              style={{ wordBreak: 'break-word' }}
            >
              {name}
            </Text>
            <Text size="xs" c="dimmed">
              {subtitle}
            </Text>
          </Stack>
        </Group>

        {actions && actions.length > 0 && (
          <motion.div
            variants={peopleRowActionsVariants}
            transition={peopleRowActionsTransition}
          >
            <Group gap={6}>
              {actions.map((action) => (
                <ActionIcon
                  key={action.label}
                  variant="subtle"
                  radius="xl"
                  size="md"
                  color={action.color ?? 'blue.6'}
                  aria-label={action.label}
                  onClick={(e) => {
                    e.stopPropagation()
                    action.onClick?.()
                  }}
                  styles={(theme) => ({
                    root: {
                      backgroundColor: 'white',
                      border: `${rem(1)} solid ${theme.colors.gray[2]}`,
                      '&:hover': {
                        backgroundColor: theme.colors.gray[1]
                      }
                    }
                  })}
                >
                  {action.icon}
                </ActionIcon>
              ))}
            </Group>
          </motion.div>
        )}
      </Group>
    </Box>
  )

  return to ? (
    <Box component={Link} to={to} display="block" td="none" c="inherit">
      {content}
    </Box>
  ) : (
    content
  )
}
